// User-facing strings for the Agent Configuration tab.

// How many of the organization's experts a scope runs. Declared once because the scope table's
// column and the editor card's header chip show the same number, and two formatters for one count
// drift.
const expertsCount = (on: number, total: number): string => `${on} of ${total}`;

const copies = {
  tabTitle: 'Agent Configuration',
  tabSubtitle: 'One configuration per sub-organization and line of business.',
  scopeTable: {
    subOrganizationColumn: 'Sub Organization',
    lobColumn: 'LOB',
    conductorColumn: 'Conductor',
    expertsColumn: 'Experts',
    actionColumn: 'Configure',
    conductorOn: 'On',
    conductorOff: 'Off',
    // How many of the experts this screen offers the scope runs, counting an
    // expert as running when either mode is set.
    expertsCount,
    emptyTitle: 'No scopes',
    emptySubtitle: 'No sub-organization / line-of-business scopes are available for this organization yet.',
  },
  editor: {
    back: 'Back',
    conductorTitle: 'Conductor',
    conductorSwitchLabel: 'Run the conductor for this scope',
    // The section's meta line, which states what the switch actually means, and no more than
    // that: off, get_active_configuration_for_scope returns None, so CMS serves no conductor
    // configuration for this scope. What the consumer then does instead is its own business and
    // is deliberately not claimed here. It is not a kill switch for the agent -- the actions
    // below are listed and executed on their own ai_integration endpoints, which never consult
    // this switch.
    conductorMetaOn: (stageCount: number) =>
      `General instructions and ${stageCount} ${stageCount === 1 ? 'stage' : 'stages'} from the claim-stage configuration`,
    conductorMetaOff: 'Off — no general instructions or stages are served for claims in this scope',
    // Scoped to the conductor's own content on purpose: it used to say "everything below stays
    // saved", which is untrue of the actions card further down — those are live whatever this
    // switch says.
    conductorOffNotice:
      'The conductor is off for this scope, so its general instructions and stages are not served for these claims. They stay saved and take effect when you turn it back on.',
    lastSaved: (when: string, who: string | null) =>
      who ? `Conductor last saved ${when} by ${who}` : `Conductor last saved ${when}`,
    generalInstructionsTitle: 'General instructions',
    generalInstructionsHelper: 'Applies to every stage. Stage-specific detail belongs in the stage’s own instructions.',
    generalInstructionsEmpty: 'No general instructions yet.',
    // Shown in the card's header, which is collapsed by default: without it the one state an
    // operator needs to notice -- nothing authored -- is behind a click.
    generalInstructionsNotSet: 'Not set',
    edit: 'Edit',
    stagesTitle: 'Stages',
    stagesCount: (count: number) => `${count} ${count === 1 ? 'stage' : 'stages'}`,
    // The header count while not-configured stages are hidden. It names both numbers rather than
    // just the shown one: the list is filtered by default, so a bare count would read as the
    // organization's whole stage list and quietly understate it.
    stagesCountShown: (shown: number, total: number) => `${shown} of ${total} ${total === 1 ? 'stage' : 'stages'}`,
    showNotConfigured: 'Show not configured',
    // The switch's own label, which the visible text beside it does not provide: that text is a
    // sibling of the control, not a <label> bound to it.
    showNotConfiguredSwitchLabel: 'Show stages that are not configured',
    // Distinct from `stagesEmpty`: the organization does have enabled stages, they are all
    // filtered out. Without this an operator who has configured nothing yet sees an empty card
    // that reads as "this organization has no stages", which is a different and false statement.
    stagesAllNotConfigured: (count: number) =>
      `None of this scope's ${count} ${count === 1 ? 'stage is' : 'stages are'} configured yet. Turn on "Show not configured" to author them.`,
    // Deliberately not the mock's "Every claim stage configured for this organization is
    // listed": a stage disabled in Claim Stages leaves this list, and its authored goal and
    // instructions stay saved but are not reachable from here until it is re-enabled.
    stagesHelper:
      'The enabled claim stages for this organization, in their configured order. Stages cannot be added, removed or reordered here — that is managed in the Claim Stages configuration screen. Disabling a stage there hides it from this list; anything authored against it is kept and returns when the stage is re-enabled.',
    stagesEmpty:
      'This organization has no enabled claim stages, so there is nothing for the conductor to work through.',
    stageConfigured: 'Configured',
    stageNotConfigured: 'Not configured',
    stageNoGoal: 'No goal set',
    savedChanges: 'Changes saved',
    savedConductorOn: 'Conductor enabled for this scope',
    savedConductorOff: 'Conductor disabled for this scope',
  },
  actions: {
    title: 'Actions',
    count: (count: number) => `${count} ${count === 1 ? 'action' : 'actions'}`,
    helper:
      'What the agent may do on a claim in this scope, and whether it may do it unattended. Each action saves on its own.',
    // The card is dimmed while the conductor is off, and this says why that is a dimming rather
    // than a disabling: an action is listed and executed on its own ai_integration endpoints,
    // which do not read the conductor's switch, so the only thing that stops one is its own
    // Enabled toggle in the edit dialog.
    conductorOffNote:
      'Turning the conductor off stops its instructions and stages, not these actions — each one stays available to the agent until you disable it here.',
    displayNameColumn: 'Display Name',
    // "Instructions", not "Description": what an operator writes here is added to the action's own
    // description, never a replacement. That description is declared in code and is not shown, so
    // the helper below is the only thing telling an operator what their text joins.
    instructionsColumn: 'Instructions',
    instructionsHelper: "Added to the action's built-in description for the agent.",
    // One string for the table column and the dialog's field: the operator edits in the dialog
    // exactly what the column shows, so the two must not drift apart.
    automaticExecution: 'Automatic Execution',
    // Shown in the Automatic Execution column for a disabled action, in place of its mode: the
    // mode describes what the agent may do with an action it can call at all, so a threshold
    // beside a disabled one would state a policy that is not in force.
    automaticExecutionDisabled: 'Disabled',
    enabledColumn: 'Enabled',
    enabledYes: 'Yes',
    enabledNo: 'No',
    guardrailsColumn: 'Guardrails',
    // Why these are two strings rather than the mock's one: see the Guardrails column in ActionsCard.
    guardrailsNotApplicable: '—',
    guardrailsNone: 'None',
    // One flowing string rather than a line per state: MUI's tooltip collapses newlines unless the
    // surface opts into `white-space: pre-line`, and TooltipIcon's contract is a plain string.
    // Names all three cells, and both reasons the dash appears. The dash stopped meaning only
    // "cannot be guarded" when the count began withholding itself until the rules arrive, and a
    // tooltip that still claimed the narrow meaning would be wrong for every in-flight read.
    guardrailsColumnTooltip:
      'How many guardrails can block this action for this scope. A number is the rules that gate it; ' +
      'None means it can be guarded but no rule does yet; — means there is no count to show, either ' +
      'because the action reports no event a guardrail can trigger on or because the rules have not ' +
      'been read.',
    editColumn: 'Edit',
    emptyTitle: 'No actions',
    emptySubtitle: 'No claim actions are available for this scope yet.',
    // Deliberately not shared with `experts.dialog.thresholdError`, which ends "or leave it
    // blank." Blank is legal there — a null threshold falls back to the expert's catalogue
    // confidence — and illegal here, because `above_threshold` is *defined* by the number and has
    // nothing to fall back to. One string for both fields would state something false about one
    // of them.
    thresholdHelper: 'Between 0 and 1. The agent acts on its own only above this confidence.',
    thresholdError: 'Enter a number between 0 and 1.',
  },
  experts: {
    title: 'Experts',
    count: expertsCount,
    // Names what each mode does. One choice per expert: an expert runs with the conductor, on
    // its own trigger, or not at all in this scope.
    helper:
      'An expert runs either with the conductor, which consults it on a claim it is working, or standalone on its own trigger, as every enabled expert does today. An expert that is off does not run in this scope at all.',
    empty: 'No expert is available to configure for this scope yet.',
    edit: 'Edit',
    nameColumn: 'Name',
    keyColumn: 'Key',
    runModeColumn: 'Run Mode',
    actionColumn: 'Edit',
    modeConductor: 'With conductor',
    modeStandalone: 'Standalone',
    modeOff: 'Off',
    saved: 'Expert configuration saved',
    dialog: {
      title: (displayName: string) => `Expert — ${displayName}`,
      runModeLabel: 'Run mode',
      modeOffLabel: 'Off',
      modeOffHelper: 'The expert does not run for this scope.',
      conductorLabel: 'With the conductor',
      conductorHelper: 'The conductor triggers the expert while it handles the claim.',
      standaloneLabel: 'Standalone (scheduled)',
      standaloneHelper: 'Runs on its own trigger, independent of the conductor.',
      // The conductor option is disabled rather than hidden when the scope's conductor is off:
      // an operator who cannot see the option cannot tell why the expert will not run with it.
      // No pointer to the standalone mode here: an expert that cannot run standalone gets this
      // banner too, beside the one saying so.
      conductorScopeOff:
        'The conductor is off for this scope, so the expert cannot run with it. Turn the conductor on in the Conductor section.',
      // A mode the expert itself cannot run is disabled the same way, whatever the scope does.
      conductorUnsupported: 'This expert does not support running with the conductor.',
      standaloneUnsupported: 'This expert does not support running standalone.',
      runModeUnsupported: 'This expert does not support this run mode. Choose another run mode.',
      thresholdLabel: 'Standalone confidence threshold',
      thresholdHelper:
        'Between 0 and 1, and used only while the expert runs standalone. Leave it blank to use the expert’s own confidence.',
      thresholdError: 'Enter a number between 0 and 1, or leave it blank.',
      instructionsHelper: 'Add any specific instructions for this expert when it reviews this scope’s claims.',
      instructionsTitle: 'Instructions',
      instructionsSubtitle:
        'Added to the expert’s own instructions for this scope. Leave it blank to use the expert’s own alone.',
    },
  },
  generalInstructionsDialog: {
    title: 'Conductor — general instructions',
    label: 'General instructions',
  },
  stageDialog: {
    title: (label: string) => `Stage — ${label}`,
    nameLabel: 'Stage name',
    keyLabel: 'Stage key',
    derivedHelper:
      'Name, key and order are defined in the organization’s claim-stage configuration and cannot be changed here.',
    goalLabel: 'Goal',
    goalPlaceholder: 'What this stage must achieve before the conductor moves on',
    goalHelper: 'One sentence. The conductor treats this as the stage’s exit condition.',
    instructionsLabel: 'Instructions',
    // Names the consequence, not just the empty fields: this alert and the Not configured chip
    // are the only things that make an unconfigured stage an informed choice. The consequence is
    // what CMS sends, not what the conductor then does -- the same line the meta copy above draws.
    notConfiguredWarning:
      'This stage is not configured. A stage needs both a goal and instructions; until it has both, it is left out of the stages sent to the conductor. You can still save and come back to it.',
  },
};

export default copies;
